import React, { useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { trackEvent } from "../lib/ga";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLES = ["Registered Investment Adviser", "Investment Adviser Representative", "Broker-Dealer", "Fund Manager", "Trust Company", "Insurance / Annuity Company", "Other"];
const SERVICES = ["Compliance Consulting", "Capital Markets Consulting", "Trust Services Consulting", "Expert Witness Services", "AML Independent Audit", "Other"];

export const ContactForm = ({ defaultTopic = "General Inquiry", defaultServices = [], compact = false, withPhone = false }) => {
  const [params] = useSearchParams();
  const topic = params.get("topic") || defaultTopic;
  const [form, setForm] = useState({ name: "", firm: "", email: "", phone: "", role: "", message: "" });
  const [services, setServices] = useState(defaultServices);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const formStarted = useRef(false);

  const onFirstInteract = () => {
    if (formStarted.current) return;
    formStarted.current = true;
    trackEvent("form_start", { form: topic, source_page: window.location.pathname });
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleService = (s) =>
    setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/api/consultation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, services, topic, source_page: window.location.pathname }),
      });
      if (!res.ok) throw new Error("Request failed");
      setSent(true);
      trackEvent("generate_lead", { service: topic, source_page: window.location.pathname });
      toast.success("Your consultation request has been sent.");
    } catch {
      toast.error("Something went wrong. Please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-white p-10 md:p-12 text-center border border-[#0A2B3D]/10" data-testid="contact-success-message">
        <div className="gold-rule mx-auto mb-6" />
        <h3 className="font-serif text-2xl md:text-3xl text-[#0A2B3D]">Thank you, {form.name.split(" ")[0]}.</h3>
        <p className="mt-4 text-[#475569] leading-relaxed max-w-md mx-auto">
          Your consultation request has been received. A member of our team will contact you shortly to discuss your needs.
        </p>
      </div>
    );
  }

  if (compact) {
    return (
      <form onSubmit={submit} onFocusCapture={onFirstInteract} className="bg-white p-6 sm:p-8 border border-[#0A2B3D]/10 shadow-2xl" data-testid="consultation-form-compact">
        <p className="eyebrow text-[#3E9BDC] mb-1.5">Request a Consultation</p>
        <h3 className="font-serif text-xl sm:text-2xl text-[#0A2B3D] mb-5">Tell us about your firm</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#0A2B3D] mb-2" htmlFor="cfc-name">Name *</label>
            <input id="cfc-name" data-testid="lp-name-input" required className="input-field" value={form.name} onChange={set("name")} placeholder="Full name" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#0A2B3D] mb-2" htmlFor="cfc-firm">Firm Name *</label>
            <input id="cfc-firm" data-testid="lp-firm-input" required className="input-field" value={form.firm} onChange={set("firm")} placeholder="Your firm" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#0A2B3D] mb-2" htmlFor="cfc-email">Email *</label>
            <input id="cfc-email" data-testid="lp-email-input" required type="email" className="input-field" value={form.email} onChange={set("email")} placeholder="you@firm.com" />
          </div>
          {withPhone && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#0A2B3D] mb-2" htmlFor="cfc-phone">Phone (optional)</label>
              <input id="cfc-phone" data-testid="lp-phone-input" type="tel" className="input-field" value={form.phone} onChange={set("phone")} placeholder="(555) 555-5555" />
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#0A2B3D] mb-2" htmlFor="cfc-message">How can we help?</label>
            <textarea id="cfc-message" data-testid="lp-message-input" className="input-field h-24 resize-none" value={form.message} onChange={set("message")} placeholder="Briefly describe your needs (optional)" />
          </div>
        </div>
        <button type="submit" disabled={sending} data-testid="lp-submit-button" className="btn-primary mt-6 w-full justify-center disabled:opacity-60">
          {sending ? <Loader2 size={16} className="animate-spin" /> : null}
          {sending ? "Sending…" : "Request a Consultation"}
        </button>
        <p className="mt-3 text-[11px] text-[#475569] text-center leading-relaxed">No obligation. A member of our team will contact you to discuss your needs.</p>
      </form>
    );
  }

  return (
    <form onSubmit={submit} onFocusCapture={onFirstInteract} className="bg-white p-8 md:p-10 border border-[#0A2B3D]/10 shadow-xl" data-testid="consultation-form">
      <p className="eyebrow text-[#3E9BDC] mb-2">Contact AEA</p>
      <h3 className="font-serif text-2xl md:text-3xl text-[#0A2B3D] mb-8">{topic}</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#0A2B3D] mb-2" htmlFor="cf-name">Name *</label>
          <input id="cf-name" data-testid="contact-name-input" required className="input-field" value={form.name} onChange={set("name")} placeholder="Full name" />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#0A2B3D] mb-2" htmlFor="cf-firm">Firm Name *</label>
          <input id="cf-firm" data-testid="contact-firm-input" required className="input-field" value={form.firm} onChange={set("firm")} placeholder="Your firm" />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#0A2B3D] mb-2" htmlFor="cf-email">Email *</label>
          <input id="cf-email" data-testid="contact-email-input" required type="email" className="input-field" value={form.email} onChange={set("email")} placeholder="you@firm.com" />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#0A2B3D] mb-2" htmlFor="cf-phone">Phone</label>
          <input id="cf-phone" data-testid="contact-phone-input" className="input-field" value={form.phone} onChange={set("phone")} placeholder="Optional" />
        </div>
      </div>

      <div className="mt-6">
        <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#0A2B3D] mb-2" htmlFor="cf-role">Let us know who you are</label>
        <select id="cf-role" data-testid="contact-role-select" className="input-field" value={form.role} onChange={set("role")}>
          <option value="">Select one</option>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      <fieldset className="mt-6">
        <legend className="text-xs font-semibold uppercase tracking-[0.15em] text-[#0A2B3D] mb-3">What services are you interested in?</legend>
        <div className="flex flex-wrap gap-2.5" data-testid="contact-services-group">
          {SERVICES.map((s) => (
            <button
              type="button"
              key={s}
              data-testid={`service-chip-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => toggleService(s)}
              className={`px-4 py-2 text-xs font-medium tracking-wide border transition-colors duration-200 ${
                services.includes(s)
                  ? "bg-[#0A2B3D] text-white border-[#0A2B3D]"
                  : "bg-white text-[#0A2B3D] border-[#0A2B3D]/30 hover:border-[#0A2B3D]"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="mt-6">
        <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#0A2B3D] mb-2" htmlFor="cf-message">Message</label>
        <textarea id="cf-message" data-testid="contact-message-input" className="input-field h-28 resize-none" value={form.message} onChange={set("message")} placeholder="Tell us briefly about your needs" />
      </div>

      <button type="submit" disabled={sending} data-testid="contact-submit-button" className="btn-primary mt-8 w-full sm:w-auto justify-center disabled:opacity-60">
        {sending ? <Loader2 size={16} className="animate-spin" /> : null}
        {sending ? "Sending…" : "Submit"}
      </button>
    </form>
  );
};
